/**
 * Corporate narrative content — shared by the homepage, /about-dilacode and
 * /company so the three surfaces cannot drift apart.
 *
 * Evidence discipline for everything in this file:
 *   - Every product listed has a live URL or a published store listing.
 *   - Every prior-work claim maps to a case study already in `src/lib/data.ts`.
 *   - Provenance is stated, not implied: work produced under the DataCode
 *     studio name is labelled as such.
 *   - Roadmap items are stated as intent. No amount is requested anywhere,
 *     and no grant, programme or eligibility is claimed.
 *   - No customers, revenue, headcount, awards, partnerships, funding,
 *     certifications, testimonials or impact percentages.
 */

export interface Pillar {
  index: string;
  title: string;
  body: string;
}

export interface OwnedProduct {
  index: string;
  name: string;
  /** What it is, in one sentence. */
  summary: string;
  /** Where it can be seen running. */
  liveLabel: string;
  liveUrl: string;
  /** Internal detail page, when we have one. */
  href?: string;
  /** Facts a reviewer can check without talking to us. */
  facts: string[];
  accent: string;
}

export interface PriorWork {
  name: string;
  summary: string;
  href: string;
  meta: string;
}

/** The one-line positioning used at the top of every corporate surface. */
export const positioning =
  'DilaCode is a Canadian software company. We build our own business software and develop products for clients.';

/** The problem statement. Descriptive, not statistical. */
export const problem = {
  eyebrow: 'The problem',
  title: 'Most business software was built for somewhere else',
  body:
    'Many CRM, ERP and point-of-sale systems assume one language, one currency and reliable internet. Businesses in Kurdistan and nearby markets often have to work around those assumptions with spreadsheets and chat messages. We build software around the way those teams actually work.',
};

/** The response. Describes what already exists, not what we hope to do. */
export const solution = {
  eyebrow: 'Our approach',
  title: 'Software that fits the operation',
  body:
    'Our systems support Kurdish, Arabic and English, cope with unreliable connections and follow the real workflow of the business. We use the same practical approach in our own products and in software we build for clients.',
  points: [
    {
      label: 'Built for three languages',
      text: 'Interfaces, content and AI replies support Kurdish, Arabic and English, including proper right-to-left layouts.',
    },
    {
      label: 'AI with human control',
      text: 'AI can draft, classify and route work. Administrators decide what can be sent automatically and what needs approval.',
    },
    {
      label: 'Works through weak connections',
      text: 'Offline support and queued updates help staff continue working when the internet drops.',
    },
    {
      label: 'Direct access to the team',
      text: 'Clients work with the people who design and build the product, from planning through release.',
    },
  ],
};

/** Products the company owns and continues to develop. */
export const ownedProducts: OwnedProduct[] = [
  {
    index: '01',
    name: 'DilaCRM',
    summary:
      'One AI inbox across WhatsApp, Instagram, Messenger, Telegram, TikTok and email, with a CRM, campaigns and an approval gate in front of every automated reply.',
    liveLabel: 'crm.dilaweb.com',
    liveUrl: 'https://crm.dilaweb.com',
    href: '/products/dilacrm',
    facts: [
      'Sales24 is the same platform under its enterprise brand, at sales24.io',
      'AI replies drafted in Kurdish, Arabic and English',
      'Administrators set what an assistant may send without a human',
    ],
    accent: '#6b93ff',
  },
  {
    index: '02',
    name: 'DilaERP',
    summary:
      'Sales, inventory, accounting, HR and payroll, POS and reporting in one system — a full ERP that works in Kurdish.',
    liveLabel: 'Live demo',
    liveUrl: 'https://app.datacode.app',
    href: '/products/dilaerp',
    facts: [
      'Fifteen modules across three languages, right-to-left included',
      'Self-hosted deployment, so the operating data stays with the business',
      'Branded document output and an in-product assistant',
    ],
    accent: '#3cc984',
  },
  {
    index: '03',
    name: 'dila.menu',
    summary:
      'Trilingual QR menus, dine-in and messaging-based ordering, and dila RMS — the restaurant management system underneath, with kitchen routing and local payment handling.',
    liveLabel: 'dila.menu',
    liveUrl: 'https://dila.menu',
    href: '/products/dilamenu',
    facts: [
      'Menu, ordering, reservations and analytics in one platform',
      'Kitchen and bar routing with offline tolerance on the floor',
      'Built for local payment behaviour, not card-only assumptions',
    ],
    accent: '#d4a574',
  },
];

/** Published mobile apps — checkable in the stores, not just described. */
export const publishedApps = [
  {
    name: 'SooqSolar',
    summary: 'Solar marketplace connecting customers with verified installers.',
    detail: 'Published on the App Store and Google Play',
    href: '/datacode-portfolio/sooqsolar',
  },
  {
    name: 'CR Medical',
    summary: 'Multi-language medical app with patient dashboard, doctor search and nurse reporting.',
    detail: 'Published on the App Store and Google Play',
    href: '/datacode-portfolio/cr-medical',
  },
];

/**
 * Selected prior work. Produced by the founder and the team under the
 * DataCode studio name — stated plainly wherever this list is rendered.
 */
export const priorWork: PriorWork[] = [
  {
    name: 'Parezar',
    summary:
      'An AI legal platform for Iraq. A multi-agent system answers legal questions, drafts contracts and searches Iraqi law across Arabic, Kurdish and English.',
    href: '/datacode-portfolio/parezar-ai-legal-platform',
    meta: 'Flutter · Next.js · Python · Rust · LLMs',
  },
  {
    name: 'SooqSolar',
    summary:
      'A solar marketplace pairing customers with verified installers, shipped to both app stores.',
    href: '/datacode-portfolio/sooqsolar',
    meta: 'Flutter · React · Node.js · PostgreSQL',
  },
  {
    name: 'Beyond',
    summary:
      'A cross-border shopping platform for UK retailers, with an explainable AI co-pilot on the retailer side.',
    href: '/datacode-portfolio/beyond-logistics',
    meta: 'Flutter · Microservices · Redis · RabbitMQ',
  },
  {
    name: 'Smart Community Platform',
    summary:
      'A residential super-app for the United Arab Emirates covering payments, solar monitoring and service workflows.',
    href: '/datacode-portfolio/smart-community-platform',
    meta: 'Flutter · Node.js · Firebase · IoT',
  },
  {
    name: 'DC-RMS',
    summary:
      'Multi-tenant restaurant management with kitchen, bar and hookah order routing, and full offline support on the floor.',
    href: '/datacode-portfolio/dc-rms-restaurant-management',
    meta: 'Flutter · Shorebird · Firestore',
  },
  {
    name: 'Batumi View',
    summary:
      'A hotel companion app for a property in Batumi, Georgia — guest services in the guest’s own language.',
    href: '/datacode-portfolio/batumi-view',
    meta: 'Flutter · Firebase · Node.js',
  },
];

/** Research and development the products already depend on. */
export const researchPillars: Pillar[] = [
  {
    index: 'R1',
    title: 'Low-resource language quality',
    body:
      'Kurdish (Sorani) and Iraqi Arabic have a fraction of the training data English does. Getting an assistant to write a usable business reply — correct register, correct script direction, correct numerals — is an open engineering problem, and it is the one our products stand on.',
  },
  {
    index: 'R2',
    title: 'Bounded autonomy',
    body:
      'Where an AI system may act alone and where it must stop and ask is a design decision, not a model property. We build the confidence scoring, the review queue and the audit trail that make that boundary explicit and adjustable per deployment.',
  },
  {
    index: 'R3',
    title: 'Evaluating output in high-stakes domains',
    body:
      'Legal, medical and financial workflows cannot accept a plausible-sounding answer. Our work on retrieval, citation and verification against source documents came out of building an AI legal platform, and it carries into every product that answers a question with consequences.',
  },
  {
    index: 'R4',
    title: 'Systems that survive bad infrastructure',
    body:
      'Intermittent connectivity, power interruption and low-end devices are the normal operating conditions for most of the businesses we build for. Offline-first data flow and predictable recovery are engineering requirements, not optional resilience work.',
  },
];

/** How a piece of work becomes a product. Describes what already happened. */
export const commercializationPath: Pillar[] = [
  {
    index: '01',
    title: 'Built for one operator',
    body:
      'A system starts as client work with a real operator, real constraints and a deadline. That is where the requirements come from — not from a market study.',
  },
  {
    index: '02',
    title: 'Generalized into a module',
    body:
      'What proves out gets pulled back into shared modules: the ordering flow, the ledger, the approval queue. The next build starts further along.',
  },
  {
    index: '03',
    title: 'Released as a product',
    body:
      'Once a module carries several operators, it becomes a product with its own release cycle, documentation and pricing. DilaERP, DilaCRM and dila.menu all arrived this way.',
  },
  {
    index: '04',
    title: 'Taken to a new market',
    body:
      'Because the products were multilingual and multi-currency from the first line, entering a new market is a configuration and go-to-market problem rather than a rewrite.',
  },
];

/** Export capability — grounded in the case studies on this site. */
export const exportCapability = {
  eyebrow: 'Export capability',
  title: 'The products travel because they were never built for one market',
  body:
    'Trilingual interfaces, right-to-left layout, multi-currency accounting and local payment behaviour are in the foundations, not in a localization backlog. The team has delivered software for clients in Iraq, Georgia, the United Arab Emirates and the United Kingdom, and has published apps to the Apple App Store and Google Play.',
  facts: [
    { label: 'Languages shipped', value: 'Kurdish, Arabic, English' },
    { label: 'Client markets delivered', value: 'Iraq, Georgia, UAE, UK' },
    { label: 'Store presence', value: 'App Store and Google Play' },
    { label: 'Deployment model', value: 'Cloud or self-hosted' },
  ],
};

/**
 * Roadmap themes. Deliberately unpriced and unquantified: these describe
 * where investment would go, not an amount, not a timeline commitment, and
 * not a claim of eligibility for any programme.
 */
export const roadmapThemes: Pillar[] = [
  {
    index: '01',
    title: 'Productization',
    body:
      'Take the three platforms from "deployable by us" to "adoptable without us": self-serve onboarding, documented configuration, tenant isolation and a support model that scales past the founding team.',
  },
  {
    index: '02',
    title: 'Applied language R&D',
    body:
      'Systematic evaluation and improvement of Kurdish and Arabic output quality for business workflows, with a test set and a measurable bar rather than spot checks.',
  },
  {
    index: '03',
    title: 'Canadian engineering capacity',
    body:
      'Build delivery and product capability in Ontario — engineering, quality and customer-facing roles attached to the products rather than to one-off projects.',
  },
  {
    index: '04',
    title: 'Market entry',
    body:
      'Position the export-ready products for Canadian and North American small and mid-sized operators, starting with the verticals we already have working software for.',
  },
  {
    index: '05',
    title: 'Security and procurement readiness',
    body:
      'The controls, documentation and processes larger buyers ask for before they will sign — built deliberately, and described only once they genuinely exist.',
  },
];

/** Rendered verbatim next to the roadmap so the framing cannot be misread. */
export const roadmapDisclaimer =
  'These are current priorities, not funding commitments. We make no claim of eligibility for any grant or funding programme.';

/** The provenance sentence. Used wherever prior work is shown. */
export const provenanceNote =
  'The work below was produced by DilaCode’s founder and team under the DataCode studio name, before and alongside the incorporation of DILACODE INC. in Ontario. It is shown as evidence of capability, not as work performed by the Canadian corporation.';
